import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useSubscriptionPricing from "common/hooks/useSubscriptionPricing";
import type {
  PricingPeriodUnit,
  RentedSubscription,
  Subscription,
  SubscriptionType,
} from "common/types";

import { post } from "../helpers/HTTP";

async function estimateCreationRequest(
  workspaceId: string,
  planId: string,
  licenseId: string,
  quantity: number,
  type: SubscriptionType,
): Promise<{ amountDue: number; nextBillingAt: number }> {
  const { estimate } = await post(
    `workspaces/${workspaceId}/subscriptions/estimates/create`,
    {
      type,
      items: [
        {
          id: planId,
        },
        ...(quantity > 0
          ? [
              {
                id: licenseId,
                quantity,
              },
            ]
          : []),
      ],
    },
  );

  const {
    invoice_estimate: invoiceEstimate,
    subscription_estimate: subscriptionEstimate,
    next_invoice_estimate: nextInvoiceEstimate,
  } = estimate;

  return {
    amountDue: invoiceEstimate
      ? invoiceEstimate.amount_due
      : nextInvoiceEstimate.amount_due,
    nextBillingAt: subscriptionEstimate.next_billing_at,
  };
}

export default function useEstimateCreation({
  subscription,
  type,
  selectedPeriodUnit,
  quantity,
}: {
  subscription: Subscription | RentedSubscription;
  type: SubscriptionType;
  selectedPeriodUnit: PricingPeriodUnit;
  quantity: number;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { plan, license } = useSubscriptionPricing(selectedPeriodUnit, type);

  const { data: estimateCreation, isLoading: isLoadingEstimateCreation } =
    useQuery({
      queryKey: [
        "estimateCreation",
        workspaceId,
        plan.id,
        license.id,
        selectedPeriodUnit,
        subscription?.id,
        type,
        quantity,
      ],
      queryFn: () =>
        estimateCreationRequest(
          workspaceId,
          plan.id,
          license.id,
          quantity,
          type,
        ),
      enabled: !subscription || subscription.status === "non_renewing",
    });

  return { estimateCreation, isLoadingEstimateCreation };
}
